/**
 * Two tax consequences of a village funding choice, said where the choice is made.
 *
 * Both were explained only inside the floating "What's happening" panel. They are the parts of a
 * village move that bite somewhere other than where the decision is taken — one of them years
 * later — which is exactly why they are easy to miss and worth putting under the switch.
 *
 * The PENSION consequences of keeping or renting the former home are covered by
 * the plan summary's In brief and the engine cards above these, and deliberately not repeated:
 * that note's whole job is to correct the intuition about which lever moves the pension.
 */
import React from 'react';
import { FieldNote } from '../../components/narrative/PlainWords.tsx';
import { formatCurrency } from '../../utils/index.ts';

/**
 * The six-year rule on a former main residence that is rented out.
 *
 * WHY IT MATTERS HERE. Renting the former home is often chosen to keep options open. What that
 * quietly starts is a clock: the main-residence CGT exemption survives for up to six years after
 * moving out, and a sale after that is partly taxable. A family that rents "for now" and sells in
 * year eight can face a bill nobody mentioned at the point they chose to rent.
 */
export function RentedFormerHomeCgtNote({ homeValue }: { homeValue: number }) {
    return (
        <FieldNote tone="info" heading="Renting starts a six-year clock for capital gains tax">
            <p>
                A former main residence can go on being treated as your main residence for CGT for up to{' '}
                <strong>six years</strong> after you move out, as long as you do not treat another dwelling as
                your main residence in that time. Sell within the six years and the gain is generally exempt.
            </p>
            <p className="mt-1.5">
                Sell later and only part of the gain is exempt — the rest is taxable, on a house
                {homeValue > 0 ? <> worth about <strong>{formatCurrency(homeValue)}</strong> today</> : null}. The
                election is made in the tax return for the year of sale. Worth confirming with the ATO or your
                accountant before the six years are up.
            </p>
        </FieldNote>
    );
}

/**
 * Selling an investment property to fund the entry contribution.
 *
 * THE POINT THAT CONFUSES PEOPLE. The capital gains tax on the sale never reaches the client's bank
 * account, so it does not appear as a cashflow cost in year one — it comes straight off the net
 * proceeds at settlement. A reader watching the cashflow line for the cost of the tax will not find
 * it there, and would be right to wonder where it went.
 */
export function InvestmentPropertyEntryFeeNote() {
    return (
        <FieldNote tone="warn" heading="Capital gains tax comes off before the money reaches the village">
            <p>
                Only the <strong>net proceeds</strong> — after selling costs and capital gains tax — go toward
                the entry contribution. So a sale price is not what the village receives, and the shortfall
                shown above is measured on the net figure.
            </p>
            <p className="mt-1.5">
                The tax reduces your net worth at settlement. It does <strong>not</strong> show up as a Year 1
                cashflow cost, because it never reaches your bank account — which is why the cashflow line will
                not appear to account for it.
            </p>
        </FieldNote>
    );
}
